import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Check, X, Loader2 } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;
const ADD_NEW = "__add_new__";

/**
 * Platform + System selectors that let a user add a brand new platform or
 * system (scoped to their own profile) when the one they need isn't listed.
 * Newly added entries can optionally be flagged for an admin to add to the
 * Master Database.
 */
export default function PlatformSystemPicker({
  platforms = [],
  systemsForPlatform = [],
  platform = "",
  system = "",
  onPlatformChange,
  onSystemChange,
  onDataRefresh,
  idPrefix = "manual",
  showSystem = true,
}) {
  const [addingPlatform, setAddingPlatform] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState("");
  const [platformSaving, setPlatformSaving] = useState(false);
  const [platformRequest, setPlatformRequest] = useState(false);

  const [addingSystem, setAddingSystem] = useState(false);
  const [newSystemName, setNewSystemName] = useState("");
  const [systemSaving, setSystemSaving] = useState(false);
  const [systemRequest, setSystemRequest] = useState(false);

  const selectedPlatform = platforms.find((p) => p.id === platform);

  const submitRequest = async (type, name, platformName) => {
    try {
      await axios.post(`${API}/platform-system-requests`, { type, name, platformName });
    } catch (err) {
      console.error("Failed to submit request:", err);
    }
  };

  const handleSavePlatform = async () => {
    const name = newPlatformName.trim();
    if (!name) return;
    setPlatformSaving(true);
    try {
      const { data } = await axios.post(`${API}/platforms/custom`, { name });
      if (platformRequest) {
        await submitRequest("platform", name, "");
      }
      if (onDataRefresh) await onDataRefresh();
      onPlatformChange?.(data.id);
      toast.success(
        platformRequest
          ? `"${name}" added & sent to the Master Database team`
          : `"${name}" added to your platforms`
      );
      setAddingPlatform(false);
      setNewPlatformName("");
      setPlatformRequest(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add platform");
    } finally {
      setPlatformSaving(false);
    }
  };

  const handleSaveSystem = async () => {
    const name = newSystemName.trim();
    if (!name || !platform) return;
    setSystemSaving(true);
    try {
      const { data } = await axios.post(`${API}/systems/custom`, {
        platformId: platform,
        name,
      });
      if (systemRequest) {
        await submitRequest("system", name, selectedPlatform?.name || platform);
      }
      if (onDataRefresh) await onDataRefresh();
      onSystemChange?.(data.name);
      toast.success(
        systemRequest
          ? `"${name}" added & sent to the Master Database team`
          : `"${name}" added to ${selectedPlatform?.name || "platform"}`
      );
      setAddingSystem(false);
      setNewSystemName("");
      setSystemRequest(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add system");
    } finally {
      setSystemSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Platform */}
      <div className="space-y-2">
        <Label>Platform *</Label>
        {addingPlatform ? (
          <div className="space-y-2 rounded-lg border border-purple-500/30 bg-purple-500/5 p-3" data-testid={`${idPrefix}-add-platform-panel`}>
            <Input
              autoFocus
              data-testid={`${idPrefix}-new-platform-input`}
              placeholder="New platform name (e.g., Dreamcast)"
              value={newPlatformName}
              onChange={(e) => setNewPlatformName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSavePlatform();
                if (e.key === "Escape") { setAddingPlatform(false); setNewPlatformName(""); }
              }}
              className="bg-black/50 border-white/10"
            />
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
              <Checkbox
                checked={platformRequest}
                onCheckedChange={(v) => setPlatformRequest(!!v)}
                data-testid={`${idPrefix}-platform-request-checkbox`}
              />
              <span>Ask an admin to add this to the Master Database</span>
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSavePlatform}
                disabled={platformSaving || !newPlatformName.trim()}
                className="bg-purple-600 hover:bg-purple-500 flex-1"
                data-testid={`${idPrefix}-save-platform-btn`}
              >
                {platformSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Add Platform</>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setAddingPlatform(false); setNewPlatformName(""); setPlatformRequest(false); }}
                className="border-white/20"
                data-testid={`${idPrefix}-cancel-platform-btn`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ) : (
          <Select
            value={platform}
            onValueChange={(value) => {
              if (value === ADD_NEW) {
                setAddingPlatform(true);
                return;
              }
              onPlatformChange?.(value);
            }}
          >
            <SelectTrigger data-testid={`${idPrefix}-platform-select`} className="bg-black/50 border-white/10">
              <SelectValue placeholder="Select platform" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              {platforms.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span style={{ color: p.color }}>{p.name}</span>
                  {p.isUserCustom && <span className="ml-1 text-[10px] text-white/30">(custom)</span>}
                </SelectItem>
              ))}
              <SelectItem value={ADD_NEW} className="text-purple-400">
                <span className="flex items-center gap-1"><Plus className="w-3 h-3" />Add new platform…</span>
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* System */}
      {showSystem && platform && (
        <div className="space-y-2">
          <Label>System *</Label>
          {addingSystem ? (
            <div className="space-y-2 rounded-lg border border-purple-500/30 bg-purple-500/5 p-3" data-testid={`${idPrefix}-add-system-panel`}>
              <Input
                autoFocus
                data-testid={`${idPrefix}-new-system-input`}
                placeholder="New system name (e.g., PlayStation 5 Pro)"
                value={newSystemName}
                onChange={(e) => setNewSystemName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveSystem();
                  if (e.key === "Escape") { setAddingSystem(false); setNewSystemName(""); }
                }}
                className="bg-black/50 border-white/10"
              />
              <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                <Checkbox
                  checked={systemRequest}
                  onCheckedChange={(v) => setSystemRequest(!!v)}
                  data-testid={`${idPrefix}-system-request-checkbox`}
                />
                <span>Ask an admin to add this to the Master Database</span>
              </label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleSaveSystem}
                  disabled={systemSaving || !newSystemName.trim()}
                  className="bg-purple-600 hover:bg-purple-500 flex-1"
                  data-testid={`${idPrefix}-save-system-btn`}
                >
                  {systemSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Add System</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setAddingSystem(false); setNewSystemName(""); setSystemRequest(false); }}
                  className="border-white/20"
                  data-testid={`${idPrefix}-cancel-system-btn`}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ) : (
            <Select
              value={system}
              onValueChange={(value) => {
                if (value === ADD_NEW) {
                  setAddingSystem(true);
                  return;
                }
                onSystemChange?.(value);
              }}
            >
              <SelectTrigger data-testid={`${idPrefix}-system-select`} className="bg-black/50 border-white/10">
                <SelectValue placeholder="Select system" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {systemsForPlatform.map((sys) => (
                  <SelectItem key={sys.id} value={sys.name}>
                    {sys.name}
                    {sys.isUserCustom && <span className="ml-1 text-[10px] text-white/30">(custom)</span>}
                  </SelectItem>
                ))}
                <SelectItem value={ADD_NEW} className="text-purple-400">
                  <span className="flex items-center gap-1"><Plus className="w-3 h-3" />Add new system…</span>
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}
